import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/compliancePermissions';
import {
  useProjectComplianceProfiles,
  type ProjectComplianceProfileDto,
  type UpsertProjectComplianceProfileDto,
} from '@/hooks/useCompliance';
import {
  PROJECT_COMPLIANCE_STATUS_LABELS,
  RISK_LEVEL_LABELS,
  RISK_LEVEL_LIST,
  formatEnumLabel,
} from '@/lib/enumLabels';

const PROJECT_COMPLIANCE_STATUSES: readonly ProjectComplianceProfileDto['status'][] = [
  'internal_research',
  'serviceable',
  'paused',
  'forbidden_sale',
  'special_approval_only',
  'expert_review_only',
];

const NEXT_STATUS_MAP: Readonly<Record<ProjectComplianceProfileDto['status'], readonly ProjectComplianceProfileDto['status'][]>> = {
  internal_research: ['serviceable', 'paused', 'forbidden_sale'],
  serviceable: ['paused', 'forbidden_sale', 'special_approval_only', 'expert_review_only', 'internal_research'],
  paused: ['serviceable', 'forbidden_sale', 'special_approval_only', 'expert_review_only', 'internal_research'],
  forbidden_sale: ['paused', 'internal_research'],
  special_approval_only: ['serviceable', 'paused', 'forbidden_sale', 'expert_review_only', 'internal_research'],
  expert_review_only: ['serviceable', 'paused', 'forbidden_sale', 'special_approval_only', 'internal_research'],
};

function riskColor(level: ProjectComplianceProfileDto['riskLevel']): string {
  if (level === 'critical') return 'bg-red-100 text-red-700';
  if (level === 'high') return 'bg-orange-100 text-orange-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

export default function ProjectStatusesPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROJECT_STATUS_READ);
  const canUpdate = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROJECT_STATUS_UPDATE);
  const { profiles, loading, error, fetchProfiles, upsertProfile, transitionProfile } = useProjectComplianceProfiles();

  const [statusFilter, setStatusFilter] = useState<ProjectComplianceProfileDto['status'] | ''>('');
  const [upsertForm, setUpsertForm] = useState<UpsertProjectComplianceProfileDto>({
    projectCode: '',
    projectName: '',
    status: 'internal_research',
    riskLevel: 'medium',
    scopeNotes: '',
    responsibleUserId: '',
  });
  const [transitionTargetId, setTransitionTargetId] = useState('');
  const [transitionNextStatus, setTransitionNextStatus] = useState<ProjectComplianceProfileDto['status']>('serviceable');
  const [transitionReason, setTransitionReason] = useState('');
  const [transitionVersion, setTransitionVersion] = useState(0);

  useEffect(() => {
    if (!canRead) {
      return;
    }
    void fetchProfiles({ status: statusFilter || undefined });
  }, [canRead, statusFilter, fetchProfiles]);

  if (!canRead) {
    return <AccessDenied resource="项目合规状态" />;
  }

  async function handleUpsert(e: React.FormEvent) {
    e.preventDefault();
    const result = await upsertProfile(upsertForm);
    if (result) {
      setUpsertForm({
        projectCode: '',
        projectName: '',
        status: 'internal_research',
        riskLevel: 'medium',
        scopeNotes: '',
        responsibleUserId: '',
      });
      void fetchProfiles({ status: statusFilter || undefined });
    }
  }

  async function handleTransition(item: ProjectComplianceProfileDto) {
    if (!canUpdate) {
      return;
    }
    const payload = {
      expectedVersion: transitionTargetId === item.id ? transitionVersion : item.version,
      nextStatus: transitionTargetId === item.id ? transitionNextStatus : NEXT_STATUS_MAP[item.status][0],
      reason: transitionTargetId === item.id ? transitionReason : `Codex UI transition from ${item.status}`,
    };
    if (!payload.nextStatus || !payload.reason) {
      return;
    }
    const result = await transitionProfile(item.id, payload);
    if (result) {
      setTransitionTargetId('');
      setTransitionReason('');
      void fetchProfiles({ status: statusFilter || undefined });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">项目合规状态</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            控制项目是否允许进入方案、报价和订单流程，并对已签客户生成复核任务。
          </p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ProjectComplianceProfileDto['status'] | '')}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">全部状态</option>
          {PROJECT_COMPLIANCE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {formatEnumLabel(status, PROJECT_COMPLIANCE_STATUS_LABELS, '未知状态')}
            </option>
          ))}
        </select>
      </div>

      {canUpdate && (
        <form onSubmit={(e) => { void handleUpsert(e); }} className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-2">
          <div className="lg:col-span-2 text-lg font-semibold">新增或覆盖项目合规档案</div>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="项目编码"
            value={upsertForm.projectCode}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, projectCode: e.target.value }))}
            required
          />
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="项目名称"
            value={upsertForm.projectName}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, projectName: e.target.value }))}
            required
          />
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={upsertForm.status}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, status: e.target.value as UpsertProjectComplianceProfileDto['status'] }))}
          >
            {PROJECT_COMPLIANCE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {formatEnumLabel(status, PROJECT_COMPLIANCE_STATUS_LABELS, '未知状态')}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={upsertForm.riskLevel}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, riskLevel: e.target.value as UpsertProjectComplianceProfileDto['riskLevel'] }))}
          >
            {RISK_LEVEL_LIST.map((level) => (
              <option key={level} value={level}>
                {RISK_LEVEL_LABELS[level]}
              </option>
            ))}
          </select>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="责任人用户 ID"
            value={upsertForm.responsibleUserId}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, responsibleUserId: e.target.value }))}
            required
          />
          <textarea
            className="min-h-24 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="范围说明"
            value={upsertForm.scopeNotes ?? ''}
            onChange={(e) => setUpsertForm((prev) => ({ ...prev, scopeNotes: e.target.value }))}
          />
          <div className="lg:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              保存项目状态
            </button>
          </div>
        </form>
      )}

      {error && <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>}

      <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">项目</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">风险</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">责任人</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">说明</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">流转</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">加载中…</td>
              </tr>
            ) : profiles.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">暂无项目状态</td>
              </tr>
            ) : (
              profiles.map((item) => {
                const nextStatuses = NEXT_STATUS_MAP[item.status];
                return (
                  <tr key={item.id} className="border-t align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium">{item.projectName}</div>
                      <div className="mt-1 font-mono text-xs text-muted-foreground">{item.projectCode}</div>
                    </td>
                    <td className="px-4 py-3">{formatEnumLabel(item.status, PROJECT_COMPLIANCE_STATUS_LABELS, '未知状态')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${riskColor(item.riskLevel)}`}>
                        {formatEnumLabel(item.riskLevel, RISK_LEVEL_LABELS, '未知')}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.responsibleUserId}</td>
                    <td className="px-4 py-3 text-gray-600">{item.scopeNotes || '—'}</td>
                    <td className="px-4 py-3">
                      {canUpdate && nextStatuses.length > 0 ? (
                        <div className="space-y-2">
                          <select
                            className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                            value={transitionTargetId === item.id ? transitionNextStatus : nextStatuses[0]}
                            onChange={(e) => {
                              setTransitionTargetId(item.id);
                              setTransitionVersion(item.version);
                              setTransitionNextStatus(e.target.value as ProjectComplianceProfileDto['status']);
                            }}
                          >
                            {nextStatuses.map((status) => (
                              <option key={status} value={status}>
                                {formatEnumLabel(status, PROJECT_COMPLIANCE_STATUS_LABELS, '未知状态')}
                              </option>
                            ))}
                          </select>
                          <input
                            className="w-full rounded border border-gray-300 px-2 py-1 text-xs"
                            placeholder="流转原因"
                            value={transitionTargetId === item.id ? transitionReason : ''}
                            onChange={(e) => {
                              setTransitionTargetId(item.id);
                              setTransitionVersion(item.version);
                              setTransitionReason(e.target.value);
                            }}
                          />
                          <button
                            onClick={() => { void handleTransition(item); }}
                            className="rounded bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700"
                          >
                            提交流转
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">无可用动作</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
